import format from 'date-fns/format';
import {LATEST_MAP_RENDERER_VERSION, systemDateFormat} from '@lib/utils';
import {IApiClient} from './IApiClient';
import {
  BuildingDto,
  SettingsDto,
  ConnectionDto,
  AppUserDto,
  PersonDTO,
  DeskDto,
  DeskSuggestionDto,
  InstallationDto,
  DeskBookingStatusDto,
  OpeningDayExceptionDto,
  NotificationPreferenceDto,
  UserNotificationPreferenceDto,
  NotificationDto,
  MeetingsSettingsDto,
  MeetingsUserPreferencesDto,
  MeetingDto,
  MeetingConsentStatusDto,
  GlobalMeetingsSettingsDto,
  CheckInStatusDto,
  MeetingRequestDto,
  MeetingSuggestionsRequestDto,
  MeetingSuggestionDto,
  MeetingInviteeSearchResultDto,
  MeetingRoomDto,
  ParkingLotDto,
  ParkingReservationDto,
  AvailabilityOfInviteesRequestDto,
  AttendeeAvailabilityDto,
  ParkingCountByDateDto,
  PreferencesDto,
  GroupWorkspaceReservationDto,
  WorkspaceReservationStatus,
  GroupWorkspaceReservationInviteeLiteDto,
  TimeZoneDto,
  ConnectionSuggestionDto,
  ConnectionSuggestionFeedbackDto,
  WorkspaceReservationDto,
  ConnectionWithReservationsDto,
  WorkspaceReservationCountByDateDto,
  AllRoomsRequestDto,
  WorkdayDto,
  MeetingRoomAvailabilityAndSuggestionDto,
  ExtraDto,
  ShiftUserWithPreferencesDto,
  FloorMapFacilityDto,
  NotificationCategoryWithTypesDto,
  WorkspaceOccupancyState,
  BuildingResourceDto,
  NoShowReaction,
  CustomerBrandingResourceDto,
  MySubscriptionResourseDto,
  UserSettingsDto,
  BuildingMeetingRoomAmenitiesDto,
  ParkingLocationDto,
  WorkspaceLocationDto,
  CreateWorkspaceReservationCommand,
  CreateWorkdayCommand,
  AnnouncementDto,
  CreateWorkspaceReservationWithPreferredLocationCommand,
  DeskAmenity,
  ActivityType,
  AppLinkDto,
  ControllableDto,
  ExtraServiceDto,
} from './apiTypes';
import {LocationFeatureCollection} from '../../../../../submodules/map/types/MpqGeoJSON';
import {ApiVersionType} from './types';

const meUri = '/shifts/me';
const myBuildingsUri = '/shifts/me/buildings';
const buildingsUri = '/shifts/buildings';
const settingUri = '/shifts/settings';
const connectionsUri = '/shifts/connections';
const connectionSuggestionsUri = `${connectionsUri}/suggestions`;
const connectionSuggestionFeedbackUri = `/SuggestionFeedback/PostConnectionFeedback`;
const peopleUri = '/shifts/people';
const desksByAreaUri = `${buildingsUri}/areas/{areaId}/desks`;
const deskBookingStatusUri = `${buildingsUri}/areas/{areaId}/deskbookingstatus`;
const deskSuggestionsUri = '/shifts/me/desksuggestions';
const mapByFloorUri = '/buildings/{buildingId}/floors/{floorId}/floorplan/svg';
const map3dByFloorUri = '/buildings/{buildingId}/floors/{floorId}/floorplan/geojson?rendererVersion=1';
const facilitiesUri = '/buildings/{buildingId}/floors/{floorId}/facilities';
const notificationsUri = '/notifications';
const notificationsCategoriesUri = '/notification-categories';
const installationUri = `${notificationsUri}/installation`;
const notificationPreferencesUri = `${notificationsUri}/users/preferences`;
const openingDaysExceptionsUri = '/shifts/me/openingdayexceptions';
const enabledFeaturesUri = '/me/enabledfeatures';
const meetingsPreferencesUri = '/meetings';
const meetingsUri = '/me/meetings';
const meetingsUserPreferencesUri = `${meetingsPreferencesUri}/me/preferences`;
const meetingConsentStatus = '/meetings/consents/status';
const connectionsCheckInStatus = '/localization/me/connections/checkin/status';
const checkInUri = '/localization/me/checkin';
const checkInStatusUri = `${checkInUri}/status`;
const meetingTimeSuggestionsUri = '/me/meetingtimesuggestions';
const profilePicturesUri = '/shifts/profilepictures';
const meetingConsentUri = '/meetings/Consents/user/consentUrl';
const meetingInviteesSearchUri = '/me/invitees';
const availabilityOfInviteesUri = '/me/calendars/availabilities';
const parkingLotsByBuildingIdUri = `${buildingsUri}/{buildingId}/parkinglots`;
const parkingLotsUri = `/shifts/parkinglots`;
const parkingReservationsUri = '/parking/me/reservations';
const parkingReservationCountUri = '/parking/reservations/count';
const userPreferencesUri = `${meUri}/preferences`;

const myPreferencesUri = '/me/preferences';
const workspacePreferencesUri = `${myPreferencesUri}/preferred-workspace`;
const parkingPreferencesUri = `${myPreferencesUri}/preferred-parking`;

const groupWorkspaceReservations = '/me/group-workspace-reservations';
const availableTimeZones = 'available-time-zones';
const availableRoomsUri = '/availabilities/availableRooms';
const workspaceReservationsUri = '/me/workspace-reservations';
const reservationCountUri = '/reservation-counts';
const connectionsReservationsUri = '/me/connections/reservations';
const allMeetingRoomsUri = '/me/rooms/suggestions';
const workdaysUri = 'me/workdays';
const extrasUri = '/ExtrasLinks';
const bookingOverviewsUri = '/me/booking-overviews';
const workspaceOccupancyStatesUri = '/buildings/{buildingId}/workspace-occupancy-states';
const noShowsUri = '/no-shows/{noShowId}';
const customerBannerUri = '/subscription/banner';
const mySubscriptionUri = '/me/subscription';
const userSetttingsUri = '/me/settings';
const announcementsUri = '/announcements';
const controlsUri = '/controllables';
const appLinksUri = '/app-links/{code}';
const extraServicesUri = '/user/extras';

let apiVersion: ApiVersionType = 'v2';

export const setGlobalApiVersion = (v: ApiVersionType) => {
  apiVersion = v;
};

export const getGlobalApiVersion = () => apiVersion;

export const mapiqApi = (apiClient: IApiClient) => ({
  postUserLogin: () => {
    return apiClient.request<AppUserDto>({url: `${meUri}/login`, method: 'post'});
  },
  getUser: () => {
    return apiClient.request<AppUserDto>({url: meUri, method: 'get'});
  },
  putUser: (user: AppUserDto) => {
    return apiClient.request<AppUserDto>({url: meUri, method: 'put', data: user});
  },
  getBuildings: () => {
    return apiClient.request<BuildingDto[]>({url: myBuildingsUri, method: 'get'});
  },
  getAvailableDeskAmenitiesForBuilding: ({buildingId}: {buildingId: string}) => {
    return apiClient.request<DeskAmenity[]>({
      url: `/buildings/${buildingId}/desk-amenities`,
      method: 'get',
      apiVersion: '1.0',
    });
  },
  getAvailableActivityTypesForBuilding: ({buildingId}: {buildingId: string}) => {
    return apiClient.request<ActivityType[]>({
      url: `/buildings/${buildingId}/workspace-activity-types`,
      method: 'get',
      apiVersion: '1.0',
    });
  },
  getDesksByAreaId: (areaId: string) => {
    const resource = desksByAreaUri.replace('{areaId}', areaId);
    return apiClient.request<DeskDto[]>({url: resource, method: 'get'});
  },
  getDeskSuggestions: (areaId: string, date: Date, deskCount?: number, currentReservationId?: string) => {
    const formattedDate = format(date, systemDateFormat);
    const urlParams = new URLSearchParams();
    urlParams.append('areaId', areaId);
    urlParams.append('date', formattedDate);
    if (deskCount) {
      urlParams.append('deskCount', deskCount.toString());
    }
    if (currentReservationId) {
      urlParams.append('currentReservationId', currentReservationId);
    }
    return apiClient.request<DeskSuggestionDto>({
      url: `${deskSuggestionsUri}?${urlParams.toString()}`,
      method: 'get',
    });
  },
  getDeskBookingStatus: (areaId: string, date: Date) => {
    const formattedDate = format(date, systemDateFormat);
    const resource = deskBookingStatusUri.replace('{areaId}', areaId);
    return apiClient.request<DeskBookingStatusDto>({url: `${resource}?date=${formattedDate}`, method: 'get'});
  },
  getMapByFloorId: (buildingId: string, floorId: string) => {
    const rendererVersionParamString = `?rendererVersion=${LATEST_MAP_RENDERER_VERSION}`;
    const baseResource = mapByFloorUri.replace('{floorId}', floorId).replace('{buildingId}', buildingId);
    const resource = `${baseResource}${rendererVersionParamString}`;
    const result = apiClient.request<string>({url: resource, method: 'get'});
    return result;
  },

  get3dMapByFloorId: (buildingId: string, floorId: string) => {
    const url = map3dByFloorUri.replace('{buildingId}', buildingId).replace('{floorId}', floorId);
    return apiClient.request<LocationFeatureCollection>({url, method: 'get', apiVersion: '1.0'});
  },

  getFacilitiesByFloorId: (buildingId: string, floorId: string) => {
    const resource = facilitiesUri.replace('{floorId}', floorId).replace('{buildingId}', buildingId);
    const result = apiClient.request<FloorMapFacilityDto[]>({url: resource, method: 'get', apiVersion: '1.0'});
    return result;
  },
  getSettings: () => {
    return apiClient.request<SettingsDto>({url: settingUri, method: 'get'});
  },
  getConnections: () => {
    return apiClient.request<ConnectionDto[]>({url: connectionsUri, method: 'get'});
  },
  postConnection: (userId: string) => {
    const data = {toUserId: userId};
    return apiClient.request<ConnectionDto>({url: connectionsUri, method: 'post', data});
  },
  deleteConnection: (connectionId: string) => {
    return apiClient.request<void>({url: `${connectionsUri}/${connectionId}`, method: 'delete'});
  },
  patchConnection: (connection: Partial<ConnectionDto>) => {
    return apiClient.request<ConnectionDto>({
      url: `${connectionsUri}/${connection.id}`,
      method: 'patch',
      data: {status: connection.status},
    });
  },
  getPeople: (searchTerm: string) => {
    return apiClient.request<PersonDTO[]>({url: `${peopleUri}?search=${searchTerm}`, method: 'get'});
  },
  postInstallation: (installation: InstallationDto) => {
    return apiClient.request<InstallationDto>({url: installationUri, method: 'post', data: installation});
  },
  deleteInstallation: (deviceId: string) => {
    return apiClient.request<void>({url: `${installationUri}/${deviceId}`, method: 'delete'});
  },
  getNotifications: () => {
    return apiClient.request<NotificationDto[]>({url: notificationsUri, method: 'get'});
  },
  getNotificationCategories: () => {
    return apiClient.request<NotificationCategoryWithTypesDto[]>({url: notificationsCategoriesUri, method: 'get'});
  },
  deleteNotification: (id: string) => {
    return apiClient.request<void>({url: `${notificationsUri}/${id}`, method: 'delete'});
  },
  patchNotifications: (notifications: Partial<NotificationDto>[]) => {
    return apiClient.request<void>({url: notificationsUri, method: 'patch', data: notifications});
  },
  getNotificationPreferences: () => {
    return apiClient.request<UserNotificationPreferenceDto>({url: notificationPreferencesUri, method: 'get'});
  },
  putNotificationPreference: (notificationPreference: NotificationPreferenceDto) => {
    return apiClient.request<NotificationPreferenceDto>({
      url: notificationPreferencesUri,
      method: 'put',
      data: notificationPreference,
    });
  },
  patchNotificationPreference: (notificationPreferences: NotificationPreferenceDto[]) => {
    return apiClient.request<NotificationPreferenceDto[]>({
      url: notificationPreferencesUri,
      method: 'patch',
      data: notificationPreferences,
    });
  },
  getOpeningDayExceptions: (from: Date, to: Date) => {
    const fromString = format(from, systemDateFormat);
    const toString = format(to, systemDateFormat);
    return apiClient.request<OpeningDayExceptionDto[]>({
      url: `${openingDaysExceptionsUri}?from=${fromString}&to=${toString}`,
      method: 'get',
    });
  },
  getEnabledFeatures: () => {
    return apiClient.request<string[]>({url: enabledFeaturesUri, method: 'get', apiVersion: '1.0'});
  },
  getGlobalMeetingSettings: () => {
    return apiClient.request<GlobalMeetingsSettingsDto>({
      url: `${meetingsPreferencesUri}/settings/global`,
      method: 'get',
      apiVersion: '1.0',
    });
  },
  getMeetingSettings: () => {
    return apiClient.request<MeetingsSettingsDto>({
      url: `${meetingsPreferencesUri}/settings`,
      method: 'get',
      apiVersion: '1.0',
    });
  },
  getMeetingUserPreferences: () => {
    return apiClient.request<MeetingsUserPreferencesDto>({
      url: meetingsUserPreferencesUri,
      method: 'get',
      apiVersion: '1.0',
    });
  },
  putWorkspacePreferences: (preferences: WorkspaceLocationDto) => {
    return apiClient.request<WorkspaceLocationDto>({
      url: workspacePreferencesUri,
      method: 'put',
      data: preferences,
    });
  },
  putParkingPreferences: (preferences: ParkingLocationDto) => {
    return apiClient.request<ParkingLocationDto>({
      url: parkingPreferencesUri,
      method: 'put',
      data: preferences,
    });
  },
  getMeetings: (from: Date, to: Date, calendarDomain: string | null | undefined) => {
    const fromString = from.toISOString();
    const toString = to.toISOString();
    const calendarDomainParam = calendarDomain?.length ? `&calendarDomain=${calendarDomain}` : '';

    return apiClient.request<MeetingDto[]>({
      url: `${meetingsUri}?startDate=${fromString}&endDate=${toString}${calendarDomainParam}`,
      method: 'get',
    });
  },
  deleteMeeting: (meetingId: string) => {
    return apiClient.request<void>({url: `${meetingsUri}/${meetingId}`, method: 'delete'});
  },
  getCalendarConsentUrl: (successRedirectUrl: string, failureRedirectUrl: string, cancelationRedirectUrl: string) => {
    return apiClient.request<string>({
      url: `${meetingConsentUri}?redirectUri=${successRedirectUrl}&redirectUriWhenRejects=${cancelationRedirectUrl}&redirectUriOnFailure=${failureRedirectUrl}`,
      method: 'get',
      apiVersion: '1.0',
    });
  },
  patchMeetingUserPreferences: (preferences: Partial<MeetingsUserPreferencesDto>) => {
    return apiClient.request<MeetingsUserPreferencesDto>({
      url: meetingsUserPreferencesUri,
      method: 'patch',
      data: preferences,
      apiVersion: '1.0',
    });
  },
  getMeetingConsentStatus: () => {
    return apiClient.request<MeetingConsentStatusDto>({url: meetingConsentStatus, method: 'get', apiVersion: '1.0'});
  },
  getConnectionsCheckInStatus: () => {
    return apiClient.request<CheckInStatusDto[]>({url: connectionsCheckInStatus, method: 'get'});
  },
  getCheckInStatus: () => {
    return apiClient.request<CheckInStatusDto>({url: checkInStatusUri, method: 'get'});
  },
  postCheckInStatus: (checkInEvent: CheckInStatusDto) => {
    return apiClient.request<CheckInStatusDto>({url: checkInUri, method: 'post', data: checkInEvent});
  },
  postCreateMeeting: (createMeetingDto: MeetingRequestDto) => {
    return apiClient.request<MeetingDto>({url: meetingsUri, method: 'post', data: createMeetingDto});
  },

  postMeetingSuggestions: (timeSuggestionParams: MeetingSuggestionsRequestDto): Promise<MeetingSuggestionDto[]> => {
    return apiClient.request<MeetingSuggestionDto[]>({
      url: meetingTimeSuggestionsUri,
      method: 'post',
      data: timeSuggestionParams,
    });
  },

  postAvailabilityOfInviteesSuggestions: (availabilitySuggestionParams: AvailabilityOfInviteesRequestDto) =>
    apiClient.request<AttendeeAvailabilityDto[]>({
      url: availabilityOfInviteesUri,
      method: 'post',
      data: availabilitySuggestionParams,
    }),

  getInvitees: (searchTerm: string) => {
    return apiClient.request<MeetingInviteeSearchResultDto[]>({
      url: `${meetingInviteesSearchUri}?search=${searchTerm}`,
      method: 'get',
    });
  },
  postProfilePicture: (image: FormData) => {
    return apiClient.request<{imageHash: string}>({
      url: profilePicturesUri,
      method: 'post',
      data: image,
    });
  },
  putProfilePicture: (image: FormData) => {
    return apiClient.request<{imageHash: string}>({
      url: profilePicturesUri,
      method: 'put',
      data: image,
    });
  },
  deleteProfilePicture: () => {
    return apiClient.request<void>({url: profilePicturesUri, method: 'delete'});
  },

  getParkingLotsByBuildingId: (buildingId: string) => {
    const resource = parkingLotsByBuildingIdUri.replace('{buildingId}', buildingId);
    return apiClient.request<ParkingLotDto[]>({url: resource, method: 'get'});
  },
  getParkingLots: () => {
    return apiClient.request<ParkingLotDto[]>({url: parkingLotsUri, method: 'get'});
  },
  getParkingReservations: (from: Date, to: Date) => {
    const fromString = format(from, systemDateFormat);
    const toString = format(to, systemDateFormat);
    return apiClient.request<ParkingReservationDto[]>({
      url: `${parkingReservationsUri}?startDate=${fromString}&endDate=${toString}`,
      method: 'get',
    });
  },
  postParkingReservation: (parkingReservation: ParkingReservationDto) => {
    return apiClient.request<ParkingReservationDto>({
      url: `${parkingReservationsUri}`,
      method: 'post',
      data: parkingReservation,
    });
  },
  getParkingReservationCountPerNode: (from: Date, to: Date) => {
    const fromString = format(from, systemDateFormat);
    const toString = format(to, systemDateFormat);
    return apiClient.request<ParkingCountByDateDto>({
      url: `${parkingReservationCountUri}?startDate=${fromString}&endDate=${toString}`,
      method: 'get',
    });
  },
  patchParkingReservation: (parkingReservation: Partial<ParkingReservationDto>) => {
    return apiClient.request<ParkingReservationDto>({
      url: `${parkingReservationsUri}/${parkingReservation.id}`,
      method: 'patch',
      data: {nodeId: parkingReservation.nodeId},
    });
  },
  deleteParkingReservation: (parkingReservationId: string) => {
    return apiClient.request<void>({
      url: `${parkingReservationsUri}/${parkingReservationId}`,
      method: 'delete',
    });
  },
  patchWorkspaceReservationPreferences: (preferences: Partial<PreferencesDto>) => {
    return apiClient.request<ShiftUserWithPreferencesDto>({
      url: userPreferencesUri,
      method: 'patch',
      data: preferences,
    });
  },
  getPreferences: () => {
    return apiClient.request<PreferencesDto>({
      url: myPreferencesUri,
      method: 'get',
    });
  },
  getGroupWorkspaceReservations: (startDate: Date, endDate: Date) => {
    const startDateString = format(startDate, systemDateFormat);
    const endDateString = format(endDate, systemDateFormat);
    const urlParams = new URLSearchParams();
    urlParams.append('startDate', startDateString);
    urlParams.append('endDate', endDateString);

    return apiClient.request<GroupWorkspaceReservationDto[]>({
      url: `${groupWorkspaceReservations}?${urlParams.toString()}`,
      method: 'get',
    });
  },
  putGroupWorkspaceReservationStatus: (
    groupWorkspaceReservationId: string,
    reservationId: string,
    reservationStatus: WorkspaceReservationStatus,
  ) => {
    return apiClient.request<GroupWorkspaceReservationInviteeLiteDto>({
      url: `${groupWorkspaceReservations}/${groupWorkspaceReservationId}/reservations/${reservationId}/status`,
      method: 'put',
      data: {
        status: reservationStatus,
      },
    });
  },
  getAvailableTimeZones: () => {
    return apiClient.request<TimeZoneDto[]>({
      url: availableTimeZones,
      method: 'get',
      apiVersion: '1.0',
    });
  },
  getConnectionSuggestions: () => {
    return apiClient.request<ConnectionSuggestionDto>({
      url: connectionSuggestionsUri,
      method: 'get',
    });
  },
  postConnectionSuggestionFeedback: (feedback: ConnectionSuggestionFeedbackDto) => {
    return apiClient.request<ConnectionSuggestionFeedbackDto>({
      url: connectionSuggestionFeedbackUri,
      method: 'post',
      data: feedback,
    });
  },
  getAvailableMeetingRooms: (
    buildingId: string,
    from: Date,
    to: Date,
    calendarDomain: string | null | undefined,
    capacity?: number,
  ) => {
    const urlParams = new URLSearchParams();
    urlParams.append('buildingId', buildingId);
    urlParams.append('startDateTime', from.toISOString());
    urlParams.append('endDateTime', to.toISOString());

    if (capacity) {
      urlParams.append('minNumberOfSeats', capacity.toString());
    }
    if (calendarDomain?.length) {
      urlParams.append('calendarDomain', calendarDomain);
    }

    return apiClient.request<MeetingRoomDto[]>({
      url: `${availableRoomsUri}?${urlParams.toString()}`,
      method: 'get',
    });
  },
  getWorkspaceReservations: (from: Date, to: Date) => {
    const fromString = format(from, systemDateFormat);
    const toString = format(to, systemDateFormat);
    return apiClient.request<WorkspaceReservationDto[]>({
      url: `${workspaceReservationsUri}?startDate=${fromString}&endDate=${toString}`,
      method: 'get',
    });
  },
  postWorkspaceReservation: (body: CreateWorkspaceReservationCommand) => {
    return apiClient.request<WorkspaceReservationDto>({
      url: workspaceReservationsUri,
      method: 'post',
      data: body,
    });
  },
  postWorkspaceReservationWithPreferredLocation: (body: CreateWorkspaceReservationWithPreferredLocationCommand) => {
    return apiClient.request<WorkspaceReservationDto>({
      url: workspaceReservationsUri,
      method: 'post',
      data: body,
    });
  },
  deleteWorkspaceReservation: (workspaceReservationId: string) => {
    return apiClient.request({url: `${workspaceReservationsUri}/${workspaceReservationId}`, method: 'delete'});
  },
  patchWorkspaceReservation: (workspaceReservation: WorkspaceReservationDto) => {
    return apiClient.request<WorkspaceReservationDto>({
      url: `${workspaceReservationsUri}/${workspaceReservation.id}`,
      method: 'patch',
      data: {nodeId: workspaceReservation.nodeId, members: workspaceReservation.members},
    });
  },
  getWorkspaceReservationCountPerNode: (from: Date, to: Date) => {
    const fromString = format(from, systemDateFormat);
    const toString = format(to, systemDateFormat);
    return apiClient.request<WorkspaceReservationCountByDateDto>({
      url: `${reservationCountUri}?from=${fromString}&to=${toString}`,
      method: 'get',
    });
  },
  getWorkspaceReservationsForConnections: (from: Date, to: Date) => {
    const fromString = format(from, systemDateFormat);
    const toString = format(to, systemDateFormat);
    return apiClient.request<ConnectionWithReservationsDto[]>({
      url: `${connectionsReservationsUri}?startDate=${fromString}&endDate=${toString}`,
      method: 'get',
    });
  },
  putMeeting: (meetingId: string, meetingData: MeetingRequestDto) => {
    return apiClient.request<MeetingDto>({
      url: `${meetingsUri}/${meetingId}`,
      data: meetingData,
      method: 'put',
    });
  },
  getWorkspaceReservation: (reservationId: string) => {
    return apiClient.request<WorkspaceReservationDto>({
      url: `${workspaceReservationsUri}/${reservationId}`,
      method: 'get',
    });
  },
  postGroupWorkspaceReservation: (groupWorkspaceReservation: GroupWorkspaceReservationDto) => {
    return apiClient.request<GroupWorkspaceReservationDto>({
      url: '/me/group-workspace-reservations',
      method: 'post',
      data: groupWorkspaceReservation,
    });
  },
  postAllMeetingRooms: (data: AllRoomsRequestDto) => {
    return apiClient.request<MeetingRoomAvailabilityAndSuggestionDto[]>({
      url: allMeetingRoomsUri,
      method: 'post',
      data,
    });
  },
  getWorkdays: (from: string, to: string) => {
    return apiClient.request<WorkdayDto[]>({
      url: `${workdaysUri}?startDate=${from}&endDate=${to}`,
      method: 'get',
    });
  },
  postWorkday: (workday: CreateWorkdayCommand) => {
    return apiClient.request<WorkdayDto>({
      url: workdaysUri,
      method: 'post',
      data: workday,
    });
  },
  putWorkday: (workday: WorkdayDto) => {
    return apiClient.request<WorkdayDto>({
      url: `${workdaysUri}/${workday.id}`,
      method: 'put',
      data: workday,
    });
  },
  getExtras: () => {
    return apiClient.request<ExtraDto[]>({url: extrasUri, method: 'get', apiVersion: '1.0'});
  },
  getExtraServices: () => {
    return apiClient.request<ExtraServiceDto[]>({
      url: extraServicesUri,
      method: 'get',
      userApiVersion: 'v2',
    });
  },
  getBookingOverviewExport: (options?: {from: string; to: string}) => {
    const params = options ? `?startDate=${options.from}&endDate=${options.to}` : '';

    return apiClient.requestRaw<string>({
      url: `${bookingOverviewsUri}${params}`,
      method: 'get',
      apiVersion: '2.0',
      headers: {
        accept: 'text/csv',
      },
    });
  },
  getWorkspaceOccupancyStatesForBuilding(buildingId: string) {
    let url = workspaceOccupancyStatesUri.replace(`{buildingId}`, buildingId);

    return apiClient.request<WorkspaceOccupancyState[]>({
      url,
      method: 'get',
      apiVersion: '1.0',
    });
  },

  getBuildingResource(buildingId: string, calendarDomain: string | null | undefined) {
    const params = calendarDomain?.length
      ? new URLSearchParams({
          calendarDomain,
        })
      : null;

    const paramsString = params === null ? '' : `?${params.toString()}`;

    return apiClient.request<BuildingResourceDto>({
      url: `/buildings/${buildingId}${paramsString}`,
      method: 'get',
      apiVersion: '1.0',
    });
  },
  putNoShowResponse: (noShowId: string, response: NoShowReaction) => {
    const url = noShowsUri.replace(`{noShowId}`, noShowId);

    return apiClient.request({
      url: `${url}/response`,
      method: 'put',
      data: response,
      apiVersion: '1.0',
    });
  },
  getMySubscription() {
    return apiClient.request<MySubscriptionResourseDto>({
      url: mySubscriptionUri,
      method: 'get',
      apiVersion: '1.0',
    });
  },
  getSubscriptionImages(hash: string, imageType: 'logo' | 'background') {
    const url = '/subscription/images';
    const urlWithSearchParams = new URLSearchParams();
    if (hash) {
      urlWithSearchParams.append('imageHash', hash);
    }
    if (imageType) {
      urlWithSearchParams.append('imageType', imageType);
    }

    const fullUrl = `${url}?${urlWithSearchParams.toString()}`;

    return apiClient.request<string>({
      url: fullUrl,
      method: 'get',
      apiVersion: '1.0',
    });
  },
  async getCustomerBranding() {
    return apiClient.request<CustomerBrandingResourceDto>({
      url: customerBannerUri,
      method: 'get',
      apiVersion: '1.0',
    });
  },
  getUserSettings: () =>
    apiClient.request<UserSettingsDto>({
      url: userSetttingsUri,
      method: 'get',
      apiVersion: '1.0',
    }),
  getBuildingMeetingRoomAmenities: () => {
    return apiClient.request<BuildingMeetingRoomAmenitiesDto>({
      url: `/buildings/meeting-room-amenities`,
      method: 'get',
      apiVersion: '1.0',
    });
  },

  // Employee Communication

  getUserAnnouncements() {
    const formattedDate = format(new Date(), systemDateFormat);

    return apiClient.request<AnnouncementDto[]>({
      url: `${announcementsUri}?localDateTime=${formattedDate}`,
      method: 'get',
    });
  },

  postDismissUserAnnouncement: (announcementId: string) => {
    return apiClient.request({
      url: `${announcementsUri}/${announcementId}`,
      method: 'patch',
      data: {isDismissed: true},
    });
  },

  // app links
  getAppLink: (appLinkId: string): Promise<AppLinkDto> => {
    let url = appLinksUri.replace(`{code}`, appLinkId);

    return apiClient.request<AppLinkDto>({
      url,
      method: 'get',
    });
  },

  // controls
  getControlsForNodeId: (nodeId: string) => {
    return apiClient.request<ControllableDto[]>({
      url: `${controlsUri}?nodeId=${nodeId}`,
      method: 'get',
    });
  },
  putTemperatureControllableValue: (controlId: string, value: number) => {
    return apiClient.request({
      url: `${controlsUri}/${controlId}/temperature`,
      method: 'put',
      data: value,
    });
  },
  putBlindsControllableValue: (controlId: string, value: number) => {
    return apiClient.request({
      url: `${controlsUri}/${controlId}/blinds-position`,
      method: 'put',
      data: value,
    });
  },
});
